import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FileText, Plus, X, Trash2, Loader2, CheckCircle2, AlertTriangle, Sparkles,
  ShieldCheck, ChevronDown, Download, MapPin,
} from "lucide-react";
import { supabase, apiFetch } from "@/lib/supabase";
import { useRequireAuth, hasRole } from "@/lib/auth";
import { useReturnTo } from "@/lib/returnTo";
import { Card, Pill, Button, Input, Select, Textarea, Spinner, EmptyState, type Tone } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { exportSwmsPdf } from "@/features/hse/swmsPdf";

// =============================================================================
// SWMS Builder — T-427 / T-427b (two tiers)
// =============================================================================
// TIER 1 — swms_library: BASE TEMPLATES per work type. Director-owned, the
//   public portal renders their approved content. The AI here IMPROVES the
//   template, never a site.
// TIER 2 — project_swms: SITE-SPECIFIC SWMS. "New site SWMS" picks a base
//   template + project, the AI merges the template with the project's real
//   context (James's Beechmont flow), a competent person edits, a director
//   approves, and it exports as a clean landscape branded PDF for clients.
//   Sign-ons target it. NEVER public unless opted in. The master template is
//   never touched.
// T-475 (07/08 external review of the Beechmont trial): HRCW categories are now
//   canonical "r 291(x) — ..." strings from the Worker's locked statutory table
//   (the AI picks letters, never composes citations), drafts carry review_flags
//   + per-hazard confidence, and a "Before you approve" panel surfaces every
//   flagged item — the director must acknowledge it before Approve enables.
// 30 Sep 2026 (mig 408) — after the Ventia audits: the FRONT MATTER a
//   principal's auditor looks for (training/competencies, permits & checks incl.
//   "SDS required", rescue plan Y/N, PPE mandatory vs task-specific, hazardous
//   substances, valid-to, reason for revision), CONSULTATION (anyone on staff
//   leaves "I've reviewed this" + a comment on a site SWMS or a base template;
//   a manager marks it considered; approval snapshots the list onto the
//   document), and RE-INDUCTION (a director requires re-acceptance after an
//   incident without bumping the version).
// =============================================================================

const RISKS = ["Low", "Medium", "High", "Extreme"] as const;
const RISK_TONE: Record<string, string> = {
  Low: "bg-ok/15 text-ok border-ok/40",
  Medium: "bg-warn/15 text-warn border-warn/40",
  High: "bg-danger/15 text-danger border-danger/40",
  Extreme: "bg-danger/30 text-danger border-danger font-semibold",
};

// T-475 — the FULL r 291 list with citations, matching the Worker's locked
// table exactly. The old 12-chip list was missing (b) telecommunication tower
// and (o) powered mobile plant — the two that apply to nearly every Coretel job.
const HRCW_CATEGORIES = [
  "r 291(a) — Risk of a person falling more than 2 metres",
  "r 291(b) — Work carried out on a telecommunication tower",
  "r 291(c) — Demolition of an element of a structure that is load-bearing or related to its physical integrity",
  "r 291(d) — Work likely to involve disturbing asbestos",
  "r 291(e) — Structural alterations or repairs that require temporary support to prevent collapse",
  "r 291(f) — Work carried out in or near a confined space",
  "r 291(g) — Work carried out in or near a shaft or trench deeper than 1.5 m, or a tunnel",
  "r 291(h) — The use of explosives",
  "r 291(i) — Work on or near pressurised gas distribution mains or piping",
  "r 291(j) — Work on or near chemical, fuel or refrigerant lines",
  "r 291(k) — Work on or near energised electrical installations or services",
  "r 291(l) — Work in an area that may have a contaminated or flammable atmosphere",
  "r 291(m) — Tilt-up or precast concrete work",
  "r 291(n) — Work on, in or adjacent to a road, railway or other traffic corridor in use by traffic other than pedestrians",
  "r 291(o) — Work in an area with movement of powered mobile plant",
  "r 291(p) — Work in areas with artificial extremes of temperature",
  "r 291(q) — Work carried out in or near water or other liquid with a risk of drowning",
  "r 291(r) — Diving work",
];

type Hazard = { hazard: string; risk_before: string; controls: string[]; risk_after: string; responsible: string; confidence?: "high" | "check"; check_note?: string };
type ReviewFlag = { kind: "legislation" | "deliverability" | "site_fact" | "scope" | "plant"; note: string; where?: string };
type JobStep = { step: string; hazards: Hazard[] };
type SwmsContent = {
  scope_of_work?: string;
  hrcw_categories?: string[];
  job_steps?: JobStep[];
  ppe?: string[];
  plant?: string[];
  permits_required?: string[];
  referenced_legislation?: string[];
  review_flags?: ReviewFlag[];
  emergency_notes?: string;
  drafted_by_ai?: boolean;
  // mig 408 — front matter
  training_required?: string[];
  permits_checks?: PermitCheck[];
  rescue_plan_required?: boolean;
  ppe_task_specific?: string[];
  hazardous_substances?: string[];
  standing_controls?: string[];     // SWMS piece B — the brain cards the draft was built on
};
type PermitCheck = { name: string; required: boolean; note?: string };
// The permits & checks every Coretel SWMS answers (modelled on a principal's
// list; "SDS required" is the one that ties the SWMS to the SDS register).
const STANDARD_CHECKS = [
  "Working at heights permit", "Rescue plan in place", "Excavation permit", "DBYD / BYDA before digging", "Hot work permit",
  "Confined space entry permit", "Energy isolation / lock-out", "RF / EME isolation", "Crane lift plan (director approved)", "Workbox permit",
  "SDS required — hazardous substances on site", "Electrical test & tag current", "Journey management plan", "Traffic management plan",
  "Client site access / permit to work", "EWP daily pre-start",
];
function withStandardChecks(list: PermitCheck[] | undefined): PermitCheck[] {
  const have = new Map((list ?? []).map(x => [x.name.toLowerCase(), x]));
  const out: PermitCheck[] = STANDARD_CHECKS.map(n => have.get(n.toLowerCase()) ?? { name: n, required: false });
  for (const x of list ?? []) if (!STANDARD_CHECKS.some(n => n.toLowerCase() === x.name.toLowerCase())) out.push(x);
  return out;
}

// T-475c — the overhead-lines question, answered by a HUMAN before generation
// (ChatGPT review SWMS-005: "do not rely on the AI to remember this hazard").
// The cockpit still red-flags any crane/EWP draft with no powerline content, so
// there are two independent nets: deterministic input + deterministic check.
const POWERLINE_OPTIONS: Record<string, string> = {
  unknown: "Overhead electric lines: NOT YET SURVEYED — treat as potentially present within the plant/boom/load envelope; survey at site establishment before any plant set-up.",
  yes: "Overhead electric lines: PRESENT within the possible plant/boom/load envelope — full line-strike controls required (exclusion zones per the electrical safety regulation and Mobile Crane CoP, dedicated spotter, electricity-entity consultation/isolation where clearance cannot be maintained).",
  no: "Overhead electric lines: CONFIRMED ABSENT from the plant/boom/load envelope by site survey — record the confirmation in the SWMS; no line-strike controls required beyond that statement.",
};

function PowerlineSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="block text-[12px] text-text-muted">Overhead electric lines near the plant/boom/load envelope?
      <Select value={value} onChange={e => onChange(e.target.value)}>
        <option value="unknown">Unknown — not surveyed yet</option>
        <option value="yes">Yes — lines within possible reach</option>
        <option value="no">No — confirmed absent by survey</option>
      </Select>
    </label>
  );
}

// T-475g \u2014 the load-support question, answered by a HUMAN before generation.
// The v1.8 Beechmont draft measured EWP capacity against a ~520 kg headframe the
// crane was holding. An EWP is a personnel work-positioning platform; asking the
// model to work out which machine carries what is exactly how that error is made.
// Two nets again: deterministic input here, deterministic check in the cockpit.
const LOAD_SUPPORT_OPTIONS: Record<string, string> = {
  crane: "Load support: every significant component (headframe, pole/shaft sections, antenna mounts, steelwork) is supported INDEPENDENTLY by the crane or hoist through landing, alignment and until fully secured and verified. Any EWP on site positions personnel only \u2014 its SWL covers occupants, tools and equipment carried in the basket and nothing else. Reference the approved lift plan rather than restating capacities.",
  platform: "Load support: all materials are carried IN the EWP platform (occupants, tools and small components only) \u2014 there is no suspended structural load in this work. Platform SWL is assessed against combined occupants, tools and carried materials.",
  attachment: "Load support: a manufacturer-approved EWP material-handling attachment is in use. The SWMS must state the machine make/model, the approved attachment and its rated capacity for the actual configuration; the permitted load is the LOWER of the manufacturer rating and any Coretel internal cap. Never assume a generic lifting figure.",
  // James, 27 Sep 2026: "we need to add manual hauling and winching on site,
  // we don't use cranes that much and the guys climb when they can."
  manual: "Load support: MANUAL HAULING AND WINCHING \u2014 there is no crane on site. Components are raised by the riggers with hand lines, a gin wheel / snatch block, a capstan or hand winch (Tirfor) rigged to a rated anchor on the structure; the riggers climb the structure on fall-arrest (twin lanyards / rope access as the SWMS states) and work-position at height. The SWMS must state the rigging method, the rated capacity of every component in the load path (rope, block, winch, anchor, shackles) against the heaviest lift, tag-line control, a marked exclusion zone under the load, no load lifted over people, and the rescue plan for the climbers. Do not describe a crane or an EWP unless one is actually on site.",
  unknown: "Load support: NOT YET CONFIRMED \u2014 do not state how any significant load is supported. Raise a review_flag kind 'plant' requiring the supervisor to confirm the lifting/support method before approval.",
};

function LoadSupportSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="block text-[12px] text-text-muted">How is each significant load supported?
      <Select value={value} onChange={e => onChange(e.target.value)}>
        <option value="manual">Manual hauling and winching \u2014 riggers climb, no crane</option>
        <option value="crane">Crane/hoist holds it \u2014 EWP positions people only</option>
        <option value="platform">Carried in the EWP platform (no suspended load)</option>
        <option value="attachment">Approved EWP material-handling attachment</option>
        <option value="unknown">Not confirmed yet</option>
      </Select>
    </label>
  );
}

function fmtDate(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

// -----------------------------------------------------------------------------
// Streamed AI draft. The worker passes the Anthropic SSE straight through —
// a big site SWMS generates for minutes, and a silent buffered request that
// long gets killed by the browser ("it won't load"). With bytes flowing the
// whole time nothing times out, and we can show live progress while the
// tool JSON is stitched back together. Guard: an empty/truncated draft is an
// error, never a save.
// -----------------------------------------------------------------------------
async function streamSwmsDraft(
  body: Record<string, unknown>,
  onProgress: (p: { chars: number; steps: number }) => void,
): Promise<SwmsContent> {
  const res = await apiFetch("/swms/ai-draft", { method: "POST", body: JSON.stringify(body) });
  if (!res.ok) {
    const j: any = await res.json().catch(() => ({}));
    throw new Error(j?.detail ?? j?.error ?? `HTTP ${res.status}`);
  }
  // Older worker build returns plain JSON — accept both during rollout.
  if ((res.headers.get("content-type") ?? "").includes("application/json")) {
    const j: any = await res.json().catch(() => ({}));
    if (!j?.draft?.job_steps?.length || !j?.draft?.scope_of_work) {
      throw new Error("The AI draft came back incomplete — hit generate again.");
    }
    return j.draft as SwmsContent;
  }
  if (!res.body) throw new Error("No response stream — try again.");

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "", json = "", inBlock = false;
  let stopReason: string | null = null;
  let streamErr: string | null = null;
  // The worker stitches the draft server-side as the SSE passes through and
  // appends a `coretel_final` event with the verified payload — that is the
  // authoritative result. Our own accumulation is just the progress counter
  // (and a fallback for an older worker build).
  let finalEv: any = null;

  const handleLine = (line: string) => {
    if (!line.startsWith("data:")) return;
    const raw = line.slice(5).trim();
    if (!raw) return;
    let ev: any;
    try { ev = JSON.parse(raw); } catch { return; }
    if (ev.type === "content_block_start") {
      inBlock = ev.content_block?.type === "tool_use";
    } else if (ev.type === "content_block_delta" && inBlock && ev.delta?.type === "input_json_delta") {
      json += ev.delta.partial_json ?? "";
      onProgress({ chars: json.length, steps: (json.match(/"step"\s*:/g) ?? []).length });
    } else if (ev.type === "content_block_stop") {
      inBlock = false;
    } else if (ev.type === "message_delta") {
      stopReason = ev.delta?.stop_reason ?? stopReason;
    } else if (ev.type === "coretel_final") {
      finalEv = ev;
    } else if (ev.type === "error") {
      streamErr = ev.error?.message ?? "stream error";
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? ""; // keep any half-received line for the next chunk
    for (const line of lines) handleLine(line);
  }
  // Flush the tail. If the stream's final bytes arrive without a trailing
  // newline, the LAST event is still sitting in buf — dropping it loses the
  // closing chars of the tool JSON and a complete draft parses as broken.
  buf += decoder.decode();
  for (const line of buf.split("\n")) handleLine(line);

  if (streamErr) throw new Error(streamErr);

  // Server-verified result wins outright — no client-side reassembly risk.
  if (finalEv) {
    if (finalEv.ok && finalEv.draft?.job_steps?.length) return finalEv.draft as SwmsContent;
    console.error("[swms-draft] server-verified incomplete", finalEv);
    throw new Error(
      `The AI draft came back incomplete (server: stop=${finalEv.stop_reason ?? "?"} · ${((finalEv.chars ?? 0) / 1000).toFixed(1)}k chars) — hit generate again.`,
    );
  }

  // Fallback (older worker build): stitch it ourselves.
  let draft: any = null;
  try { draft = JSON.parse(json); } catch { /* incomplete JSON → guard below */ }
  if (!draft || !Array.isArray(draft.job_steps) || draft.job_steps.length === 0 || !draft.scope_of_work) {
    // Leave a forensic trail in the console — tail of the JSON tells us
    // whether it was truncated (missing closers) or structurally off.
    console.error("[swms-draft] incomplete", {
      stopReason, chars: json.length,
      parsed: !!draft, steps: draft?.job_steps?.length ?? null,
      tail: json.slice(-250),
    });
    const diag = `stop=${stopReason ?? "?"} · ${(json.length / 1000).toFixed(1)}k chars`;
    throw new Error(stopReason === "max_tokens"
      ? `The draft hit the output limit (${diag}) — generate again.`
      : `The AI draft came back incomplete (${diag}) — hit generate again.`);
  }
  return { ...draft, drafted_by_ai: true } as SwmsContent;
}

export default function SwmsBuilderScreen() {
  const { swmsId, projectSwmsId } = useParams<{ swmsId: string; projectSwmsId: string }>();
  if (projectSwmsId) return <SiteSwmsEditor projectSwmsId={projectSwmsId}/>;
  if (swmsId) return <TemplateEditor swmsId={swmsId}/>;
  return <SwmsLibraryList/>;
}

// =============================================================================
// Library list — templates + site-specific
// =============================================================================
function SwmsLibraryList() {
  const user = useRequireAuth();
  const isDirector = hasRole(user, "director");
  const canEdit = hasRole(user, "manager");
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [siteFlow, setSiteFlow] = useState(false);
  const [newCode, setNewCode] = useState("SWMS-");
  const [newName, setNewName] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ["swms_builder_list"],
    queryFn: async () => {
      const { data, error } = await supabase.from("swms_library")
        .select("swms_id, swms_code, display_name, scope_of_work, is_active, is_high_risk_construction_work, content_status, content_version, hazards_json")
        .order("swms_code");
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const { data: siteSwms = [] } = useQuery({
    queryKey: ["project_swms_list"],
    queryFn: async () => {
      const { data, error } = await supabase.from("project_swms")
        .select("project_swms_id, swms_number, title, status, version, created_at, approved_at, projects:project_id(project_code, project_name, sites:site_id(site_name))")
        .neq("status", "superseded")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  async function createTemplate() {
    if (!newCode.trim() || !newName.trim()) return;
    setErr(null);
    const { data, error } = await supabase.from("swms_library").insert({
      swms_code: newCode.trim().toUpperCase(),
      display_name: newName.trim(),
      is_active: false,
      is_high_risk_construction_work: true,
    }).select("swms_id").single();
    if (error) { setErr(`${error.message}${!isDirector ? " (creating templates is director-only)" : ""}`); return; }
    navigate(`/hse/swms/${data.swms_id}`);
  }

  const contentTone = (s: any): [string, Tone] =>
    s.content_status === "approved" ? [`approved v${s.content_version}`, "ok"]
    : s.content_status === "draft" ? ["draft", "warning"]
    : ["no structured content", "default"];

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto space-y-5">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">HSE</div>
          <h1 className="text-2xl font-semibold tracking-tight">SWMS</h1>
          <p className="text-text-muted text-[13px] mt-1">
            Base templates per work type → AI-generated site-specific SWMS per project → director approval → branded PDF.
          </p>
        </div>
        <div className="flex gap-2">
          {isDirector && <Button variant="secondary" icon={Plus} onClick={() => setAdding(true)}>New template</Button>}
          {canEdit && <Button icon={MapPin} onClick={() => setSiteFlow(true)}>New site SWMS</Button>}
        </div>
      </div>

      {isLoading && <div className="flex justify-center py-8"><Spinner size={18}/></div>}

      {/* Site-specific */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Site-specific SWMS · {siteSwms.length}</div>
        {siteSwms.length === 0 && (
          <Card><div className="p-5 text-[13px] text-text-muted italic">None yet — "New site SWMS" takes a base template + your project's real conditions and drafts the site version. The master template is never touched.</div></Card>
        )}
        {siteSwms.length > 0 && (
          <Card>
            <div className="divide-y divide-line/40">
              {siteSwms.map(s => (
                <button key={s.project_swms_id} type="button" onClick={() => navigate(`/hse/swms/site/${s.project_swms_id}`)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap hover:bg-ink-raised/40 transition">
                  <span className="font-mono text-[12px] text-accent w-[110px]">{s.swms_number}</span>
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-[13px] text-text">{s.title}</div>
                    <div className="text-[11px] text-text-muted">{s.projects?.project_code} · {s.projects?.sites?.site_name ?? s.projects?.project_name}</div>
                  </div>
                  <Pill label={s.status === "approved" ? `approved v${s.version}` : "draft — not for use"} tone={s.status === "approved" ? "ok" : "warning"} small/>
                </button>
              ))}
            </div>
          </Card>
        )}
      </div>

      {/* Templates */}
      <div>
        <div className="text-[11px] uppercase tracking-wider text-text-muted mb-2">Base templates · {templates.length}</div>
        {templates.length > 0 && (
          <Card>
            <div className="divide-y divide-line/40">
              {templates.map(s => {
                const [label, tone] = contentTone(s);
                return (
                  <button key={s.swms_id} type="button" onClick={() => navigate(`/hse/swms/${s.swms_id}`)}
                    className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap hover:bg-ink-raised/40 transition">
                    <span className="font-mono text-[12px] text-accent w-[110px]">{s.swms_code}</span>
                    <div className="flex-1 min-w-[220px]">
                      <div className="text-[13px] text-text">{s.display_name}</div>
                      {s.scope_of_work && <div className="text-[11px] text-text-muted truncate max-w-[420px]">{s.scope_of_work}</div>}
                    </div>
                    {s.is_high_risk_construction_work && <Pill label="HRCW" tone="critical" small/>}
                    <Pill label={label} tone={tone} small/>
                    <Pill label={s.is_active ? "active" : "inactive"} tone={s.is_active ? "ok" : "default"} small/>
                  </button>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {adding && (
        <ModalShell title="New base template" onClose={() => setAdding(false)}>
          {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
          <label className="block text-[12px] text-text-muted">Code
            <Input value={newCode} onChange={e => setNewCode(e.target.value)} mono placeholder="SWMS-007"/>
          </label>
          <label className="block text-[12px] text-text-muted">Title
            <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="e.g. Antenna & RRU Replacement — Live Site"/>
          </label>
          <Button onClick={createTemplate} disabled={!newCode.trim() || !newName.trim()} className="w-full justify-center">Create + open builder</Button>
        </ModalShell>
      )}

      {siteFlow && (
        <NewSiteSwmsModal
          templates={templates}
          onClose={() => setSiteFlow(false)}
          onCreated={id => { setSiteFlow(false); navigate(`/hse/swms/site/${id}`); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// New site SWMS — template + project + description → AI → project_swms draft
// =============================================================================
function NewSiteSwmsModal({ templates, onClose, onCreated }: {
  templates: any[]; onClose: () => void; onCreated: (id: string) => void;
}) {
  const [baseId, setBaseId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [powerlines, setPowerlines] = useState("unknown");
  const [loadSupport, setLoadSupport] = useState("manual");   // 27 Sep 2026 — our usual: riggers climb, no crane
  const [progress, setProgress] = useState<{ chars: number; steps: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const { data: projects = [] } = useQuery({
    queryKey: ["site_swms_projects"],
    queryFn: async () => {
      const { data } = await supabase.from("projects")
        .select("project_id, project_code, project_name, site_id, sites:site_id(site_name, state, address_line1, suburb, latitude, longitude), clients:client_id(legal_name)")
        .in("status", ["scheduled", "won", "mobilising", "live", "on_hold", "acceptance_pending"])
        .order("project_code", { ascending: false });
      return (data ?? []) as any[];
    },
  });

  const base = templates.find(t => t.swms_id === baseId);
  const project = projects.find(p => p.project_id === projectId);

  async function generate() {
    if (!projectId || !description.trim()) { setErr("Pick a project and describe the work."); return; }
    setBusy(true); setErr(null);
    try {
      const site = project?.sites;
      const state = (site?.state ?? "QLD").toUpperCase();
      const siteContext = [
        POWERLINE_OPTIONS[powerlines],
        LOAD_SUPPORT_OPTIONS[loadSupport],
        site?.site_name ? `Site: ${site.site_name}` : "",
        site?.address_line1 ? `${site.address_line1}${site.suburb ? ", " + site.suburb : ""}` : "",
        site?.latitude != null && site?.longitude != null ? `GPS: ${site.latitude}, ${site.longitude}` : "",
        project?.clients?.legal_name ? `Client: ${project.clients.legal_name}` : "",
      ].filter(Boolean).join(". ");

      const draft = await streamSwmsDraft({
        work_description: description.trim(),
        state,
        site_context: siteContext || undefined,
        base_content: base?.hazards_json ?? undefined,
      }, setProgress);

      const title = `${base?.display_name ?? "SWMS"} — ${site?.site_name ?? project?.project_name ?? ""}`.trim();
      const { data: id, error } = await supabase.schema("app").rpc("save_project_swms", {
        p_project_swms_id: null,
        p_project_id: projectId,
        p_base_swms_id: baseId || null,
        p_title: title,
        p_content: draft,
        p_site_context: description.trim(),
      });
      if (error) throw error;
      onCreated(id as string);
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  return (
    <ModalShell title="New site-specific SWMS" onClose={onClose} wide>
      <div className="bg-ink rounded-md border border-line/50 p-3 text-[12px] text-text-muted leading-relaxed">
        Pick the base template + project, then describe the actual works and site conditions — structure type,
        ground, access, powerlines, public interface, wind exposure, anything real. The AI merges the template with
        your conditions into a <b className="text-text">site draft</b>. The master template is never touched, and
        nothing is usable until a <b className="text-text">director approves</b>.
      </div>
      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block text-[12px] text-text-muted">Base template
          <Select value={baseId} onChange={e => setBaseId(e.target.value)}>
            <option value="">None — from scratch</option>
            {templates.map(t => <option key={t.swms_id} value={t.swms_id}>{t.swms_code} · {t.display_name}</option>)}
          </Select>
        </label>
        <label className="block text-[12px] text-text-muted">Project *
          <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
            <option value="">Pick…</option>
            {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.project_code} · {p.sites?.site_name ?? p.project_name}</option>)}
          </Select>
        </label>
      </div>
      {project?.sites?.state && (
        <div className="text-[11px] text-text-muted">Legislation state auto-set from the site: <span className="text-accent font-mono">{project.sites.state}</span></div>
      )}
      <label className="block text-[12px] text-text-muted">Describe the works + site conditions *
        <Textarea rows={7} value={description} onChange={e => setDescription(e.target.value)}
          placeholder="e.g. Greenfield new build (no existing carriers). Erect 45m monopole, slip-jointed sections by mobile crane from a closed public road, EWP fit-up to 45m, hilltop wind region B1, shared community carpark. Programme: delivery 24 Aug, pole stand 14 Sep — site stored unattended between."/>
      </label>
      <PowerlineSelect value={powerlines} onChange={setPowerlines}/>
        <LoadSupportSelect value={loadSupport} onChange={setLoadSupport}/>
      <Button onClick={generate} disabled={busy || !projectId || !description.trim()} icon={busy ? Loader2 : Sparkles} className="w-full justify-center">
        {busy
          ? progress
            ? `Writing the draft… step ${Math.max(progress.steps, 1)} · ${(progress.chars / 1000).toFixed(1)}k chars`
            : "Reading the job + site conditions…"
          : "Generate site SWMS draft"}
      </Button>
      {busy && (
        <div className="text-[11px] text-text-muted text-center">
          A detailed job can take a few minutes — the counter above is the draft being written live. Leave this open.
        </div>
      )}
    </ModalShell>
  );
}

// =============================================================================
// Template editor (tier 1)
// =============================================================================
function TemplateEditor({ swmsId }: { swmsId: string }) {
  const user = useRequireAuth();
  const isDirector = hasRole(user, "director");
  const canEdit = hasRole(user, "manager");
  const returnTo = useReturnTo();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: swms, isLoading } = useQuery({
    queryKey: ["swms_builder", swmsId],
    queryFn: async () => {
      const { data, error } = await supabase.from("swms_library")
        .select("swms_id, swms_code, display_name, scope_of_work, is_active, hazards_json, content_status, content_version, content_approved_at, referenced_legislation")
        .eq("swms_id", swmsId).single();
      if (error) throw error;
      return data as any;
    },
  });

  const editor = useContentEditor({
    initial: swms ? seedContent(swms.hazards_json, swms.scope_of_work, swms.referenced_legislation) : null,
    ready: !!swms,
    save: async content => {
      const { error } = await supabase.schema("app").rpc("save_swms_draft", { p_swms_id: swmsId, p_content: content });
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["swms_builder", swmsId] });
    },
    approve: async () => {
      const { data, error } = await supabase.schema("app").rpc("approve_swms_content", { p_swms_id: swmsId });
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["swms_builder", swmsId] });
      qc.invalidateQueries({ queryKey: ["swms_builder_list"] });
      return data as number;
    },
    afterApprove: () => navigate("/hse/swms"),
  });

  if (isLoading || !swms || !editor.content) return <div className="py-20 flex justify-center"><Spinner/></div>;

  return (
    <ContentEditorView
      backTo={returnTo ?? "/hse/swms"} backLabel={returnTo ? "Site folder" : "SWMS library"}
      code={swms.swms_code} title={swms.display_name}
      statusPill={swms.content_status === "approved" && !editor.dirty
        ? <Pill label={`approved v${swms.content_version} · ${fmtDate(swms.content_approved_at)}`} tone="ok"/>
        : <Pill label="DRAFT — not for use" tone="warning"/>}
      banner="TEMPLATE — this is the master. Site jobs get their own copy via 'New site SWMS'; the AI here improves the template itself."
      editor={editor}
      canEdit={canEdit} isDirector={isDirector}
      approveLabel={`Approve template v${(swms.content_version ?? 0) + 1}`}
      aiExistingLabel="AI: improve the template"
      pdf={null}
      consultation={<ConsultationPanel swmsId={swmsId} version={swms.content_version ?? 0} lastApprovedAt={swms.content_approved_at} user={user} canConsider={canEdit}/>}
      approvedNote={swms.content_status === "approved" && !editor.dirty
        ? `Approved v${swms.content_version} · ${fmtDate(swms.content_approved_at)} — edit or AI-rework it to raise a new version`
        : null}
    />
  );
}

// =============================================================================
// Site SWMS editor (tier 2)
// =============================================================================
function SiteSwmsEditor({ projectSwmsId }: { projectSwmsId: string }) {
  const user = useRequireAuth();
  const isDirector = hasRole(user, "director");
  const canEdit = hasRole(user, "manager");
  const returnTo = useReturnTo();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data: row, isLoading } = useQuery({
    queryKey: ["project_swms", projectSwmsId],
    queryFn: async () => {
      // approver join — the PDF's APPROVED BY box was hardcoded null, so a
      // signed SWMS printed with a blank approver (T-427b).
      const { data, error } = await supabase.from("project_swms")
        .select(`*, projects:project_id(project_code, project_name, clients:client_id(legal_name), sites:site_id(site_name, state, address_line1, suburb, latitude, longitude)), base:base_swms_id(swms_code, display_name), approver:approved_by_user_id(login_email, employees:employee_id(first_name, last_name))`)
        .eq("project_swms_id", projectSwmsId).single();
      if (error) throw error;
      return data as any;
    },
  });

  const editor = useContentEditor({
    initial: row ? ((row.content ?? {}) as SwmsContent) : null,
    ready: !!row,
    save: async content => {
      const { error } = await supabase.schema("app").rpc("save_project_swms", {
        p_project_swms_id: projectSwmsId,
        p_project_id: row.project_id,
        p_base_swms_id: row.base_swms_id,
        p_title: row.title,
        p_content: content,
      });
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["project_swms", projectSwmsId] });
    },
    approve: async front => {
      // mig 408 — reason + valid-to travel with the approval; the RPC snapshots the consultation list
      const { data, error } = await supabase.schema("app").rpc("approve_project_swms", {
        p_project_swms_id: projectSwmsId,
        p_review_reason: front?.reason ?? null,
        p_valid_to: front?.validTo ?? null,
      } as any);
      if (error) throw error;
      qc.invalidateQueries({ queryKey: ["project_swms", projectSwmsId] });
      qc.invalidateQueries({ queryKey: ["project_swms_list"] });
      return data as number;
    },
    // Back to the register once signed — the page used to sit there offering
    // "Approve v2", which errored on a second press.
    afterApprove: () => navigate("/hse/swms"),
  });

  // T-427 — opt-in portal publishing. A site SWMS names a client's site and
  // conditions, so it is NEVER public by default; a director publishes it
  // deliberately (e.g. a client asks for it to be visible).
  async function togglePublish(next: boolean) {
    if (next && !window.confirm(
      `Publish ${row.swms_number} on the PUBLIC HSE portal?\n\nAnyone with the link will see the site name, client and full method. Only do this if you're happy for it to be public.`,
    )) return;
    const { error } = await supabase.from("project_swms")
      .update({ publish_public: next }).eq("project_swms_id", projectSwmsId);
    if (error) { window.alert(`Couldn't change publishing: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["project_swms", projectSwmsId] });
  }

  // mig 408 — re-induction: a director requires every worker to re-accept this
  // version (incident, new hazard, periodic) without changing the document.
  async function requireReacceptance() {
    const reason = window.prompt("Why must the crew re-accept this SWMS? (printed on the document and shown in the site folder)", "Incident on site — re-read before the next shift");
    if (!reason?.trim()) return;
    const { error } = await (supabase as any).schema("app").rpc("swms_require_reacceptance", { p_project_swms_id: projectSwmsId, p_reason: reason.trim() });
    if (error) { window.alert(error.message); return; }
    qc.invalidateQueries({ queryKey: ["project_swms", projectSwmsId] });
  }

  if (isLoading || !row || !editor.content) return <div className="py-20 flex justify-center"><Spinner/></div>;

  const approverName = row.approver
    ? ([row.approver.employees?.first_name, row.approver.employees?.last_name].filter(Boolean).join(" ").trim()
       || row.approver.login_email || null)
    : null;

  const downloadPdf = () => exportSwmsPdf({
    swmsNumber: row.swms_number,
    title: row.title,
    version: row.version || 0,
    status: editor.dirty ? "draft" : row.status,
    projectCode: row.projects?.project_code,
    projectName: row.projects?.project_name,
    siteName: row.projects?.sites?.site_name,
    clientName: row.projects?.clients?.legal_name,
    state: row.projects?.sites?.state ?? null,
    // T-475 — a DRAFT (or edited-since-approval) export must not carry the
    // previous approval: "APPROVED 31 July" on a draft generated in August was
    // an external-review finding. The boxes print blank until a director
    // approves THIS content; the RPC re-stamps both on approval.
    approvedBy: row.status === "approved" && !editor.dirty ? approverName : null,
    approvedAt: row.status === "approved" && !editor.dirty ? row.approved_at : null,
    baseTemplate: row.base ? `${row.base.swms_code} · ${row.base.display_name}` : null,
    content: editor.content,
    // mig 408 — the control record (blank on a draft, like the approval boxes)
    validTo: row.status === "approved" && !editor.dirty ? row.valid_to : null,
    reviewReason: row.review_reason ?? null,
    consulted: row.status === "approved" && !editor.dirty ? (row.consulted_json ?? []) : null,
    reacceptAfter: row.reaccept_after ?? null,
    reacceptReason: row.reaccept_reason ?? null,
  }).catch(e => window.alert(`PDF failed: ${e?.message ?? e}`));

  return (
    <ContentEditorView
      backTo={returnTo ?? "/hse/swms"} backLabel={returnTo ? "Site folder" : "SWMS"}
      code={row.swms_number} title={row.title}
      subtitle={`${row.projects?.project_code} · ${row.projects?.sites?.site_name ?? row.projects?.project_name}${row.base ? ` · from ${row.base.swms_code}` : ""}`}
      statusPill={row.status === "approved" && !editor.dirty
        ? <>
            <Pill label={`approved v${row.version} · ${fmtDate(row.approved_at)}`} tone="ok"/>
            {row.valid_to && <Pill label={`valid to ${fmtDate(row.valid_to)}`} tone={row.valid_to < new Date().toISOString().slice(0, 10) ? "critical" : "default"} small/>}
            {row.reaccept_after && <Pill label="re-acceptance required" tone="warning" small/>}
          </>
        : <Pill label="DRAFT — not for use" tone="warning"/>}
      banner={row.status === "approved" && !editor.dirty
        ? (row.reaccept_after ? `Re-acceptance required since ${fmtDate(row.reaccept_after)}: ${row.reaccept_reason ?? ""}` : null)
        : "Site working copy — crews must not use this until a director approves it. The base template is untouched."}
      editor={editor}
      canEdit={canEdit} isDirector={isDirector}
      approveLabel={`Approve site SWMS v${(row.version ?? 0) + 1}`}
      approveNeedsReason
      consultation={<ConsultationPanel projectSwmsId={projectSwmsId} version={row.version ?? 0} lastApprovedAt={row.approved_at} user={user} canConsider={canEdit}/>}
      aiExistingLabel="AI: rework this site draft"
      aiSiteFacts={{
        state: row.projects?.sites?.state ?? null,
        context: [
          row.projects?.sites?.site_name ? `Site: ${row.projects.sites.site_name}` : "",
          [row.projects?.sites?.address_line1, row.projects?.sites?.suburb, row.projects?.sites?.state].filter(Boolean).join(", "),
          row.projects?.sites?.latitude != null && row.projects?.sites?.longitude != null
            ? `GPS: ${row.projects.sites.latitude}, ${row.projects.sites.longitude}` : "",
          row.projects?.clients?.legal_name ? `Client: ${row.projects.clients.legal_name}` : "",
        ].filter(Boolean).join(". "),
      }}
      pdf={{ onClick: downloadPdf, draft: row.status !== "approved" || editor.dirty }}
      approvedNote={row.status === "approved" && !editor.dirty
        ? `Approved v${row.version} · ${fmtDate(row.approved_at)}${approverName ? ` by ${approverName}` : ""}`
        : null}
      extraHeader={isDirector && row.status === "approved" && !editor.dirty ? (
        <label className="flex items-center gap-2 text-[12px] text-text-muted cursor-pointer">
          <input type="checkbox" checked={!!row.publish_public}
            onChange={e => void togglePublish(e.target.checked)}
            className="accent-accent"/>
          {row.publish_public
            ? <span className="text-ok">Published on the public portal</span>
            : <span>Publish on the public portal</span>}
          <button type="button" onClick={() => void requireReacceptance()} className="ml-auto text-[12px] text-warn hover:underline">Require re-acceptance…</button>
        </label>
      ) : null}
    />
  );
}

// =============================================================================
// Consultation (mig 408) — the record that a worker read this SWMS and what
// they said, and that a manager considered it. Anyone on staff can leave one;
// the reviews since the last approval print on the next version as "Workers
// consulted". Directors consult; the record is of the workers.
// =============================================================================
function ConsultationPanel({ projectSwmsId, swmsId, version, lastApprovedAt, user, canConsider }: {
  projectSwmsId?: string; swmsId?: string; version: number; lastApprovedAt?: string | null; user: any; canConsider: boolean;
}) {
  const qc = useQueryClient();
  const key = ["swms_reviews", projectSwmsId ?? swmsId];
  const [kind, setKind] = useState<"reviewed" | "comment" | "change_request">("reviewed");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { data: rows = [] } = useQuery({
    queryKey: key,
    queryFn: async () => {
      let q = (supabase as any).from("swms_reviews")
        .select("review_id, swms_version, user_id, reviewed_at, kind, comment, considered_at, response, reviewer:users!swms_reviews_user_id_fkey(login_email, employees:employee_id(first_name, last_name)), considerer:users!swms_reviews_considered_by_fkey(login_email, employees:employee_id(first_name, last_name))")
        .order("reviewed_at", { ascending: false }).limit(100);
      q = projectSwmsId ? q.eq("project_swms_id", projectSwmsId) : q.eq("swms_id", swmsId);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });
  const who = (u: any) => [u?.employees?.first_name, u?.employees?.last_name].filter(Boolean).join(" ") || u?.login_email || "—";
  const mineToday = rows.some(r => r.user_id === user?.user_id && r.reviewed_at > new Date(Date.now() - 86400e3).toISOString());
  const sinceLast = rows.filter(r => !lastApprovedAt || r.reviewed_at > lastApprovedAt);

  async function leave() {
    if (kind !== "reviewed" && !comment.trim()) { setErr("Say what you'd change."); return; }
    setBusy(true); setErr(null);
    const { error } = await (supabase as any).from("swms_reviews").insert({
      project_swms_id: projectSwmsId ?? null, swms_id: swmsId ?? null, swms_version: version || 1,
      user_id: user.user_id, kind, comment: comment.trim() || null,
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setComment(""); setKind("reviewed");
    qc.invalidateQueries({ queryKey: key });
  }
  async function consider(r: any) {
    const response = window.prompt(`Outcome for ${who(r.reviewer)}'s ${r.kind === "change_request" ? "change request" : "comment"}:\n"${r.comment ?? ""}"`, r.kind === "change_request" ? "Changed in this revision" : "Noted");
    if (response === null) return;
    const { error } = await (supabase as any).schema("app").rpc("swms_review_consider", { p_review_id: r.review_id, p_response: response.trim() || null });
    if (error) { window.alert(error.message); return; }
    qc.invalidateQueries({ queryKey: key });
  }

  return (
    <Card>
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div>
            <div className="text-[13px] font-semibold flex items-center gap-2"><ShieldCheck size={15} className="text-accent"/> Consultation — who has reviewed this</div>
            <div className="text-[11px] text-text-muted">Every review since the last approval prints on the next version as "Workers consulted". A comment or change request stays open until a manager records the outcome.</div>
          </div>
          <Pill label={`${sinceLast.length} since ${lastApprovedAt ? `v${version}` : "first draft"}`} tone={sinceLast.length ? "ok" : "default"} small/>
        </div>

        {/* leave a review — anyone on staff */}
        <div className="rounded-lg border border-line bg-ink p-3 space-y-2">
          <div className="flex gap-1.5 flex-wrap">
            {([["reviewed", "I've read it — no comment"], ["comment", "Comment"], ["change_request", "Request a change"]] as const).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setKind(k)}
                className={`text-[11px] px-2.5 py-1 rounded-full border ${kind === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>{label}</button>
            ))}
          </div>
          {kind !== "reviewed" && <Textarea rows={2} value={comment} onChange={e => setComment(e.target.value)} placeholder={kind === "change_request" ? "What should change, and why — e.g. the rescue kit is at the ute, not the base of the tower" : "What you noticed"}/>}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => void leave()} disabled={busy}>{busy ? "Saving…" : kind === "reviewed" ? "Record that I've reviewed it" : "Send"}</Button>
            {mineToday && <span className="text-[11px] text-ok inline-flex items-center gap-1"><CheckCircle2 size={12}/> Recorded today</span>}
            {err && <span className="text-[11px] text-danger">{err}</span>}
          </div>
        </div>

        {rows.length > 0 && (
          <div className="divide-y divide-line/40">
            {rows.map(r => (
              <div key={r.review_id} className="py-2 flex items-start gap-3 text-[12px]">
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-text">{who(r.reviewer)}</span>
                  <span className="text-text-muted"> · {fmtDate(r.reviewed_at)} · v{r.swms_version ?? "—"} · {r.kind === "reviewed" ? "read" : r.kind === "comment" ? "comment" : "change request"}</span>
                  {r.comment && <div className="text-text mt-0.5">{r.comment}</div>}
                  {r.considered_at
                    ? <div className="text-[11px] text-ok mt-0.5">→ {r.response || "considered"} · {who(r.considerer)} · {fmtDate(r.considered_at)}</div>
                    : r.kind !== "reviewed" && <div className="text-[11px] text-warn mt-0.5">open — awaiting a manager's outcome</div>}
                </div>
                {canConsider && r.kind !== "reviewed" && !r.considered_at && (
                  <Button size="sm" variant="secondary" onClick={() => void consider(r)}>Record outcome</Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// mig 408 — the director's approval dialog for a site SWMS: why this version, how long it stands.
function ApproveModal({ nextVersion, onClose, onApprove }: { nextVersion: number; onClose: () => void; onApprove: (front: { reason: string; validTo: string | null }) => void }) {
  const in12 = new Date(); in12.setFullYear(in12.getFullYear() + 1);
  const [reason, setReason] = useState(nextVersion <= 1 ? "First issue" : "");
  const [validTo, setValidTo] = useState(in12.toISOString().slice(0, 10));
  return (
    <ModalShell title={`Approve site SWMS v${nextVersion}`} onClose={onClose}>
      <p className="text-[12px] text-text-muted">By approving you confirm you have reviewed it as a competent person and it adequately addresses the hazards of this work. It becomes the version in force; the reviews left since the last version are recorded on it as the consultation.</p>
      <label className="block text-[12px] text-text-muted">Reason for this version *
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="First issue · Incident on site 30 Sep · Scope change — crane added · Periodic review"/>
      </label>
      <label className="block text-[12px] text-text-muted">Valid to (review by)
        <Input type="date" value={validTo} onChange={e => setValidTo(e.target.value)}/>
        <span className="block text-[11px] mt-0.5">12 months is the default; a shorter job can end with the job.</span>
      </label>
      <div className="flex gap-2 justify-end pt-1">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button icon={ShieldCheck} disabled={!reason.trim()} onClick={() => onApprove({ reason: reason.trim(), validTo: validTo || null })}>Approve v{nextVersion}</Button>
      </div>
    </ModalShell>
  );
}

// =============================================================================
// Shared editor state hook + view
// =============================================================================
function useContentEditor({ initial, ready, save, approve, afterApprove }: {
  initial: SwmsContent | null; ready: boolean;
  save: (c: SwmsContent) => Promise<void>;
  approve: (front?: { reason: string; validTo: string | null }) => Promise<number>;
  afterApprove?: () => void;
}) {
  const [content, setContent] = useState<SwmsContent | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<"save" | "approve" | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (ready && initial && !content) setContent(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  function patch(p: Partial<SwmsContent>) {
    setContent(prev => ({ ...(prev ?? {}), ...p }));
    setDirty(true);
    setSavedFlash(false);
  }

  async function doSave(): Promise<boolean> {
    if (!content) return false;
    setBusy("save"); setErr(null);
    try {
      await save(content);
      setDirty(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      return true;
    } catch (e: any) {
      setErr(e?.message ?? String(e));
      return false;
    } finally {
      setBusy(null);
    }
  }

  // mig 408 — a site SWMS approval carries the reason for the revision and the
  // valid-to date (the ApproveModal collects them); a template approval doesn't.
  async function doApprove(front?: { reason: string; validTo: string | null }) {
    if (!front && !window.confirm(
      "Approve this SWMS?\n\nBy approving you confirm you have reviewed it as a competent person and it adequately addresses the hazards of this work. It becomes the version in force.")) return;
    if (dirty && !(await doSave())) return;
    setBusy("approve"); setErr(null);
    try {
      const v = await approve(front);
      window.alert(`Approved — now in force as v${v}.`);
      afterApprove?.();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(null);
    }
  }

  return { content, patch, setContent, dirty, setDirty, busy, savedFlash, err, doSave, doApprove };
}

function seedContent(hazardsJson: any, scope?: string | null, legislation?: string[] | null): SwmsContent {
  const base = (hazardsJson ?? {}) as SwmsContent;
  return {
    scope_of_work: base.scope_of_work ?? scope ?? "",
    hrcw_categories: base.hrcw_categories ?? [],
    job_steps: base.job_steps ?? [],
    ppe: base.ppe ?? [],
    plant: base.plant ?? [],
    permits_required: base.permits_required ?? [],
    referenced_legislation: base.referenced_legislation ?? legislation ?? [],
    review_flags: base.review_flags,
    emergency_notes: base.emergency_notes ?? "",
    drafted_by_ai: base.drafted_by_ai,
  };
}

// =============================================================================
// T-475 — the review cockpit. One list of everything a director should eyeball
// before approving: the AI's own uncertainty flags, hazards it marked 'check',
// unresolved author-instructions ("confirm exact route…"), residual High/Extreme
// ratings, and crane/EWP work with no overhead-powerline content. Computed LIVE
// from the content, so fixing an item clears its flag.
// =============================================================================
type ReviewFinding = { tone: "danger" | "warn"; title: string; detail: string };

function computeReviewFindings(content: SwmsContent): ReviewFinding[] {
  const out: ReviewFinding[] = [];
  const steps = content.job_steps ?? [];
  for (const f of content.review_flags ?? []) {
    const title = f.kind === "legislation" ? "Legislation to verify"
      : f.kind === "deliverability" ? "Confirm we can actually deliver this control"
      : f.kind === "site_fact" ? "Site assumption — check it's true"
      : f.kind === "plant" ? "Load support / plant capacity — resolve before approval"
      : "Scope — confirm this work is ours";
    out.push({ tone: f.kind === "plant" ? "danger" : "warn", title, detail: f.note + (f.where ? ` (${f.where})` : "") });
  }
  steps.forEach((js, si) => (js.hazards ?? []).forEach(h => {
    if (h.confidence === "check") out.push({
      tone: "warn", title: `Step ${si + 1} — hazard flagged for competent-person check`,
      detail: `${h.hazard}${h.check_note ? ` — ${h.check_note}` : ""}`,
    });
  }));
  // Author-instructions left in the document. Strict pattern for controls
  // ("confirm isolation before work" is a legitimate control); looser for
  // scope/emergency/permits, where "confirm…" is always an unresolved note.
  const strictRe = /(TBC|to be confirmed|confirm exact|confirm route|placeholder|\?\?\?)/i;
  const looseRe = /(TBC|to be confirmed|confirm|verify)/i;
  const scan = (txt: string | undefined | null, where: string, re: RegExp) => {
    if (txt && re.test(txt)) out.push({ tone: "danger", title: "Unresolved detail — an approved SWMS can't contain notes to its author", detail: `${where}: "${String(txt).slice(0, 120)}"` });
  };
  scan(content.scope_of_work, "Scope", looseRe);
  scan(content.emergency_notes, "Emergency notes", looseRe);
  (content.permits_required ?? []).forEach(x => scan(x, "Permits", looseRe));
  steps.forEach((js, si) => (js.hazards ?? []).forEach(h => (h.controls ?? []).forEach(ctl => scan(ctl, `Step ${si + 1} control`, strictRe))));
  steps.forEach((js, si) => (js.hazards ?? []).forEach(h => {
    if (h.risk_after === "High" || h.risk_after === "Extreme") out.push({
      tone: "danger", title: `Residual ${h.risk_after} — step ${si + 1}`,
      detail: `"${h.hazard}" stays ${h.risk_after} after controls. Either further practicable controls belong in here, or approving is a formal, deliberate acceptance of ${h.risk_after} residual risk.`,
    });
  }));
  const all = JSON.stringify(content).toLowerCase();
  const plantHit = /crane|ewp|elevated work|boom lift|travel tower|concrete pump/.test(all);
  const linesHit = /overhead (power|electric|line)|power ?line|electric line|energex|essential energy|ausgrid|endeavour energy/.test(all);
  if (plantHit && !linesHit) out.push({
    tone: "danger", title: "Overhead electric lines not addressed",
    detail: "This SWMS involves crane/EWP/elevated plant but says nothing about overhead powerlines. Add the hazard (survey at setup, exclusion zone, spotter, electricity-entity consultation) — or record their confirmed absence.",
  });
  // T-475g — an EWP rated against a load it does not carry. Catches hand-typed
  // content too, not just what the generator wrote.
  const ewpRe = /\bewp\b|elevated work platform|boom lift|travel tower|scissor lift/i;
  const capRe = /\bswl\b|safe working load|rated capacity|load chart|lifting capacity|capacity of/i;
  const perRe = /occupant|personnel|worker|rigger|person|tools|basket contents|equipment carried/i;
  const compRe = /head ?frame|pole section|shaft section|monopole section|antenna|rru|dish|structural steel|mount|assembly/i;
  steps.forEach((js, si) => (js.hazards ?? []).forEach(h => (h.controls ?? []).forEach(ctl => {
    const t = String(ctl);
    if (ewpRe.test(t) && capRe.test(t) && !perRe.test(t) && compRe.test(t)) out.push({
      tone: "danger", title: `Step ${si + 1} — EWP measured against a load it doesn't carry`,
      detail: `"${t.slice(0, 130)}" — an EWP positions people; its SWL covers occupants, tools and equipment in the basket. If a crane holds that component, state that it stays independently supported until secured and verified, and reference the approved lift plan instead of restating capacities.`,
    });
  })));
  return out;
}

function ContentEditorView({ backTo, backLabel, code, title, subtitle, statusPill, banner, editor, canEdit, isDirector, approveLabel, aiExistingLabel, pdf, approvedNote, extraHeader, aiSiteFacts, consultation, approveNeedsReason }: {
  backTo: string; backLabel: string; code: string; title: string; subtitle?: string;
  statusPill: React.ReactNode; banner: string | null;
  editor: ReturnType<typeof useContentEditor>;
  canEdit: boolean; isDirector: boolean; approveLabel: string; aiExistingLabel: string;
  pdf: { onClick: () => void; draft: boolean } | null;
  // Set when the doc is already approved and unedited — the approve button is
  // swapped for a done-state so a second press can't raise
  // nothing_to_approve:status_approved (T-427b).
  approvedNote?: string | null;
  extraHeader?: React.ReactNode;   // e.g. the site-SWMS portal publish toggle
  // T-475b — the site's REAL stored facts (state, address, GPS, client), fed to
  // the AI so it grounds instead of guessing. The rework path used to send only
  // the description — that's how a Gold Coast hinterland site became "remote
  // rural" and a greenfield build grew live-carrier RF controls.
  aiSiteFacts?: { state?: string | null; context?: string | null };
  // mig 408 — the consultation panel (site SWMS or template) and whether
  // Approve collects a reason + valid-to (site SWMS) or just confirms (template).
  consultation?: React.ReactNode;
  approveNeedsReason?: boolean;
}) {
  const [aiOpen, setAiOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const checks = withStandardChecks(editor.content!.permits_checks);
  function setCheck(name: string, patchC: Partial<PermitCheck>) {
    editor.patch({ permits_checks: withStandardChecks(editor.content!.permits_checks).map(x => x.name === name ? { ...x, ...patchC } : x) });
  }
  const content = editor.content!;
  const readOnly = !canEdit;
  // T-475 — approval gate: while flagged items exist, the director must tick
  // the acknowledgement before Approve enables. Re-arms if the findings change.
  const findings = computeReviewFindings(content);
  const [flagsAcked, setFlagsAcked] = useState(false);
  useEffect(() => { setFlagsAcked(false); }, [findings.length]);

  return (
    <div className="p-4 md:p-6 max-w-[1000px] mx-auto pb-32 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <Link to={backTo} className="text-[12px] text-text-muted hover:text-text">← {backLabel}</Link>
          <div className="flex items-center gap-2 flex-wrap mt-1">
            <span className="font-mono text-[13px] text-accent">{code}</span>
            {statusPill}
            {content.drafted_by_ai && <Pill label="AI-assisted" tone="notice" small/>}
          </div>
          <h1 className="text-xl font-semibold tracking-tight mt-0.5">{title}</h1>
          {subtitle && <div className="text-[12px] text-text-muted mt-0.5">{subtitle}</div>}
        </div>
        <div className="flex gap-2">
          {pdf && (
            <Button variant="secondary" icon={Download} onClick={pdf.onClick}>
              PDF{pdf.draft ? " (draft w/ watermark)" : ""}
            </Button>
          )}
          {canEdit && (
            <Button variant="secondary" icon={Sparkles} onClick={() => setAiOpen(true)}>
              {content.job_steps?.length ? aiExistingLabel : "AI: generate draft"}
            </Button>
          )}
        </div>
      </div>

      {extraHeader}
      {editor.err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{editor.err}</div>}
      {banner && (
        <div className="bg-warn/10 border border-warn/40 rounded-md px-4 py-2.5 text-[12px] text-warn">
          <AlertTriangle size={12} className="inline mr-1.5 -mt-0.5"/> {banner}
        </div>
      )}

      {/* Scope + classifications */}
      <Card>
        <div className="p-4 space-y-4">
          <label className="block text-[12px] text-text-muted">Scope of work
            <Textarea rows={3} value={content.scope_of_work ?? ""} disabled={readOnly}
              onChange={e => editor.patch({ scope_of_work: e.target.value })}/>
          </label>
          <div>
            <div className="text-[12px] text-text-muted mb-1.5">High-risk construction work categories (WHS Reg 291)</div>
            <div className="flex flex-wrap gap-1.5">
              {HRCW_CATEGORIES.map(h => {
                const on = (content.hrcw_categories ?? []).some(x => x.toLowerCase().includes(h.toLowerCase().slice(0, 25)) || h.toLowerCase().includes(x.toLowerCase().slice(0, 25)));
                return (
                  <button key={h} type="button" disabled={readOnly}
                    onClick={() => {
                      const cur = content.hrcw_categories ?? [];
                      editor.patch({ hrcw_categories: on ? cur.filter(x => !(x.toLowerCase().includes(h.toLowerCase().slice(0, 25)) || h.toLowerCase().includes(x.toLowerCase().slice(0, 25)))) : [...cur, h] });
                    }}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition text-left ${on ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
                    {h}
                  </button>
                );
              })}
            </div>
          </div>
          <ListEditor label="PPE required" items={content.ppe ?? []} disabled={readOnly} onChange={v => editor.patch({ ppe: v })} placeholder="e.g. Harness + dual lanyard"/>
          <ListEditor label="Plant & equipment" items={content.plant ?? []} disabled={readOnly} onChange={v => editor.patch({ plant: v })} placeholder="e.g. EWP, 5t excavator"/>
          <ListEditor label="Permits required" items={content.permits_required ?? []} disabled={readOnly} onChange={v => editor.patch({ permits_required: v })} placeholder="e.g. Working at heights"/>
          <ListEditor label="Referenced legislation & codes" items={content.referenced_legislation ?? []} disabled={readOnly} onChange={v => editor.patch({ referenced_legislation: v })} placeholder="e.g. WHS Act 2011 (QLD)"/>
          <label className="block text-[12px] text-text-muted">Emergency notes
            <Textarea rows={2} value={content.emergency_notes ?? ""} disabled={readOnly}
              onChange={e => editor.patch({ emergency_notes: e.target.value })}
              placeholder="Rescue arrangements, muster, comms — the start card carries the day-specific details"/>
          </label>
        </div>
      </Card>

      {/* mig 408 — front matter: what an auditor reads before the method */}
      <Card>
        <div className="p-4 space-y-4">
          <div>
            <div className="text-[13px] font-semibold">Front matter — training, permits & checks, rescue, substances</div>
            <div className="text-[11px] text-text-muted">The control record a principal's auditor reads first. Tick what this work needs; the PDF prints the whole list with ☒ / ☐ so "not needed" is a decision, not an omission.</div>
          </div>
          <ListEditor label="Training, licences & competencies required" items={content.training_required ?? []} disabled={readOnly} onChange={v => editor.patch({ training_required: v })} placeholder="e.g. RIIWHS204 Work safely at heights · HRWL Rigger (Basic) · WP licence + VOC on the EWP · Tower rescue"/>
          <div>
            <div className="text-[12px] text-text-muted mb-1.5">Permits & checks</div>
            <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1">
              {checks.map(x => (
                <label key={x.name} className={`flex items-start gap-2 text-[12px] py-0.5 ${x.required ? "text-text" : "text-text-muted"}`}>
                  <input type="checkbox" checked={x.required} disabled={readOnly} onChange={e => setCheck(x.name, { required: e.target.checked })} className="accent-accent mt-0.5"/>
                  <span className="flex-1">{x.name}
                    {x.required && !readOnly && (
                      <input value={x.note ?? ""} onChange={e => setCheck(x.name, { note: e.target.value })} placeholder="note — number, who holds it, when"
                        className="block w-full mt-0.5 bg-ink border border-line rounded px-2 py-0.5 text-[11px] text-text placeholder:text-text-muted"/>
                    )}
                    {x.required && readOnly && x.note && <span className="block text-[11px] text-text-muted">{x.note}</span>}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-[12px] text-text-muted">Rescue plan required?</span>
            {([[true, "Yes — separate document, before the climb"], [false, "No — no work at height or in a confined space"]] as const).map(([v, label]) => (
              <label key={String(v)} className="flex items-center gap-1.5 text-[12px] text-text">
                <input type="radio" name="rescue" checked={content.rescue_plan_required === v} disabled={readOnly} onChange={() => editor.patch({ rescue_plan_required: v })} className="accent-accent"/> {label}
              </label>
            ))}
          </div>
          <ListEditor label="PPE — task specific (on top of the site-mandatory PPE above)" items={content.ppe_task_specific ?? []} disabled={readOnly} onChange={v => editor.patch({ ppe_task_specific: v })} placeholder="e.g. Face shield + glasses on the grinder · Gumboots in a wet pit · Personal EME monitor"/>
          <ListEditor label="Hazardous substances on this job (SDS held on the register)" items={content.hazardous_substances ?? []} disabled={readOnly} onChange={v => editor.patch({ hazardous_substances: v })} placeholder="e.g. CRC 5-56 · Loctite 243 · Diesel"/>
          {!!content.standing_controls?.length && (
            <div className="text-[11px] text-text-muted">Drafted on Coretel's standing controls: {content.standing_controls.join(" · ")}</div>
          )}
        </div>
      </Card>

      {consultation}

      {/* Job steps */}
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-wider text-text-muted">Job steps · {content.job_steps?.length ?? 0}</div>
        {!readOnly && (
          <Button size="sm" variant="secondary" icon={Plus}
            onClick={() => editor.patch({ job_steps: [...(content.job_steps ?? []), { step: "", hazards: [] }] })}>
            Add step
          </Button>
        )}
      </div>
      {(content.job_steps ?? []).map((js, si) => (
        <StepCard key={si} index={si} step={js} disabled={readOnly}
          onChange={next => {
            const steps = [...(content.job_steps ?? [])];
            steps[si] = next;
            editor.patch({ job_steps: steps });
          }}
          onRemove={() => editor.patch({ job_steps: (content.job_steps ?? []).filter((_, i) => i !== si) })}
        />
      ))}
      {(content.job_steps ?? []).length === 0 && (
        <Card><div className="p-6 text-center text-[13px] text-text-muted italic">No job steps yet — add them manually or let the AI draft a starting point.</div></Card>
      )}

      {findings.length > 0 && !approvedNote && (
        <Card>
          <div className="p-4 space-y-2.5">
            <div className="flex items-center gap-2">
              <ShieldCheck size={15} className="text-warn"/>
              <span className="text-[13px] font-semibold">Before you approve — {findings.length} item{findings.length === 1 ? "" : "s"} to eyeball</span>
            </div>
            <p className="text-[11px] text-text-muted">
              Every control in an approved SWMS is an enforceable commitment. Red items need fixing or a deliberate
              call; amber items are things the AI wasn't sure of. Fixing an item in the document clears it from this list.
            </p>
            <div className="space-y-1.5">
              {[...findings].sort((x, y) => (x.tone === "danger" ? 0 : 1) - (y.tone === "danger" ? 0 : 1)).map((f, i) => (
                <div key={i} className={`rounded border px-3 py-2 text-[12px] ${f.tone === "danger" ? "border-danger/40 bg-danger/5" : "border-warn/40 bg-warn/5"}`}>
                  <span className={f.tone === "danger" ? "text-danger font-medium" : "text-warn font-medium"}>{f.title}</span>
                  <span className="block text-text-muted mt-0.5">{f.detail}</span>
                </div>
              ))}
            </div>
            {isDirector && (
              <label className="flex items-start gap-2 text-[12px] cursor-pointer pt-1">
                <input type="checkbox" checked={flagsAcked} onChange={e => setFlagsAcked(e.target.checked)} className="accent-accent mt-0.5"/>
                <span>I have reviewed each item above as part of my competent-person review. Approving commits Coretel to every control in this document.</span>
              </label>
            )}
          </div>
        </Card>
      )}

      <StickyBottomBar className="p-3 flex justify-center">
        <div className="flex gap-2 max-w-[1000px] w-full">
          {canEdit && (
            <Button variant="secondary" size="lg" onClick={() => void editor.doSave()} disabled={editor.busy != null}>
              {editor.busy === "save" ? <Loader2 size={14} className="animate-spin"/>
                : editor.savedFlash ? <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-ok"/> Saved</span>
                : editor.dirty ? "Save draft *" : "Save draft"}
            </Button>
          )}
          {approvedNote ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-[13px] text-ok">
              <CheckCircle2 size={15}/> {approvedNote}
              {pdf && <button type="button" onClick={pdf.onClick} className="text-accent hover:underline ml-2">Download PDF</button>}
            </div>
          ) : isDirector && (
            <Button size="lg" icon={editor.busy === "approve" ? undefined : ShieldCheck} onClick={() => approveNeedsReason ? setApproveOpen(true) : void editor.doApprove()}
              disabled={editor.busy != null || (content.job_steps ?? []).length === 0 || (findings.length > 0 && !flagsAcked)}
              className="flex-1 justify-center">
              {editor.busy === "approve" ? <Loader2 size={14} className="animate-spin"/> : approveLabel}
            </Button>
          )}
          {!approvedNote && !isDirector && canEdit && (
            <div className="flex-1 flex items-center justify-center text-[12px] text-text-muted">Director approval publishes it.</div>
          )}
        </div>
      </StickyBottomBar>

      {approveOpen && (
        <ApproveModal nextVersion={Number((approveLabel.match(/v(\d+)/) ?? [])[1] ?? 1)} onClose={() => setApproveOpen(false)}
          onApprove={front => { setApproveOpen(false); void editor.doApprove(front); }}/>
      )}

      {aiOpen && (
        <AiDraftModal
          siteFacts={aiSiteFacts}
          defaultDescription={content.scope_of_work ?? title}
          existing={(content.job_steps?.length ?? 0) > 0 ? content : null}
          onClose={() => setAiOpen(false)}
          onDraft={draft => {
            editor.setContent({ ...draft, drafted_by_ai: true });
            editor.setDirty(true);
            setAiOpen(false);
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Step card + bits
// =============================================================================
function StepCard({ index, step, disabled, onChange, onRemove }: {
  index: number; step: JobStep; disabled?: boolean;
  onChange: (s: JobStep) => void; onRemove: () => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <Card>
      <div className="px-4 py-2.5 flex items-center gap-3 border-b border-line/40">
        <button type="button" onClick={() => setOpen(v => !v)} className="flex items-center gap-2 flex-1 text-left">
          <span className="font-mono text-[11px] text-accent">{String(index + 1).padStart(2, "0")}</span>
          <ChevronDown size={13} className={`text-text-muted transition-transform ${open ? "" : "-rotate-90"}`}/>
        </button>
        <Input className="flex-[3]" value={step.step} disabled={disabled} placeholder="Job step — what's being done"
          onChange={e => onChange({ ...step, step: e.target.value })}/>
        {!disabled && (
          <button type="button" onClick={onRemove} className="text-text-muted hover:text-danger p-1"><Trash2 size={13}/></button>
        )}
      </div>
      {open && (
        <div className="p-4 space-y-3">
          {step.hazards.map((h, hi) => (
            <div key={hi} className="bg-ink rounded-md border border-line/50 p-3 space-y-2.5">
              {(h.confidence === "check" || h.check_note) && (
                <div className="flex items-start gap-2 text-[11px] text-warn bg-warn/10 border border-warn/30 rounded px-2 py-1.5">
                  <AlertTriangle size={11} className="mt-0.5 shrink-0"/>
                  <span className="flex-1">AI flagged this for a competent-person check{h.check_note ? ` — ${h.check_note}` : ""}.</span>
                  {!disabled && (
                    <button type="button" className="text-ok hover:underline shrink-0"
                      onClick={() => { const hazards = [...step.hazards]; hazards[hi] = { ...h, confidence: "high", check_note: undefined }; onChange({ ...step, hazards }); }}>
                      mark checked
                    </button>
                  )}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Input className="flex-1" value={h.hazard} disabled={disabled} placeholder="Hazard"
                  onChange={e => {
                    const hazards = [...step.hazards]; hazards[hi] = { ...h, hazard: e.target.value };
                    onChange({ ...step, hazards });
                  }}/>
                {!disabled && (
                  <button type="button" onClick={() => onChange({ ...step, hazards: step.hazards.filter((_, i) => i !== hi) })}
                    className="text-text-muted hover:text-danger p-1"><Trash2 size={12}/></button>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <RiskSelect label="Before" value={h.risk_before} disabled={disabled}
                  onChange={v => { const hazards = [...step.hazards]; hazards[hi] = { ...h, risk_before: v }; onChange({ ...step, hazards }); }}/>
                <span className="text-text-muted text-[11px]">→ controls →</span>
                <RiskSelect label="After" value={h.risk_after} disabled={disabled}
                  onChange={v => { const hazards = [...step.hazards]; hazards[hi] = { ...h, risk_after: v }; onChange({ ...step, hazards }); }}/>
                <Input className="flex-1 min-w-[140px]" value={h.responsible} disabled={disabled} placeholder="Responsible (role)"
                  onChange={e => { const hazards = [...step.hazards]; hazards[hi] = { ...h, responsible: e.target.value }; onChange({ ...step, hazards }); }}/>
              </div>
              <Textarea rows={Math.max(2, h.controls.length)} disabled={disabled}
                value={h.controls.join("\n")}
                placeholder={"Controls — one per line, hierarchy first (eliminate → engineer → admin → PPE)"}
                onChange={e => {
                  const hazards = [...step.hazards];
                  hazards[hi] = { ...h, controls: e.target.value.split("\n") };
                  onChange({ ...step, hazards });
                }}
                onBlur={e => {
                  const hazards = [...step.hazards];
                  hazards[hi] = { ...h, controls: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) };
                  onChange({ ...step, hazards });
                }}/>
            </div>
          ))}
          {!disabled && (
            <Button size="sm" variant="secondary" icon={Plus}
              onClick={() => onChange({ ...step, hazards: [...step.hazards, { hazard: "", risk_before: "Medium", controls: [], risk_after: "Low", responsible: "All crew" }] })}>
              Add hazard
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}

function RiskSelect({ label, value, onChange, disabled }: { label: string; value: string; onChange: (v: string) => void; disabled?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] uppercase tracking-wider text-text-muted">{label}</span>
      <div className="flex gap-1">
        {RISKS.map(r => (
          <button key={r} type="button" disabled={disabled} onClick={() => onChange(r)} title={r}
            className={`text-[10px] px-2 py-1 rounded border transition ${value === r ? RISK_TONE[r] : "border-line text-text-dim hover:text-text"}`}>
            {r[0]}
          </button>
        ))}
      </div>
    </div>
  );
}

function ListEditor({ label, items, onChange, disabled, placeholder }: {
  label: string; items: string[]; onChange: (v: string[]) => void; disabled?: boolean; placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  function add() {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v]);
    setDraft("");
  }
  return (
    <div>
      <div className="text-[12px] text-text-muted mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-1.5">
        {items.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1.5 bg-ink border border-line/60 rounded-full pl-2.5 pr-1.5 py-1 text-[12px]">
            {it}
            {!disabled && (
              <button type="button" onClick={() => onChange(items.filter((_, ii) => ii !== i))}
                className="text-text-muted hover:text-danger p-0.5"><X size={11}/></button>
            )}
          </span>
        ))}
        {items.length === 0 && <span className="text-[12px] text-text-dim italic">None</span>}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Input value={draft} onChange={e => setDraft(e.target.value)} placeholder={placeholder}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}/>
          <Button size="sm" variant="secondary" onClick={add} disabled={!draft.trim()}>Add</Button>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// AI draft modal (in-editor rework — site creation has its own flow)
// =============================================================================
function AiDraftModal({ defaultDescription, existing, onClose, onDraft, siteFacts }: {
  defaultDescription: string; existing: SwmsContent | null;
  onClose: () => void; onDraft: (d: SwmsContent) => void;
  siteFacts?: { state?: string | null; context?: string | null };
}) {
  const [description, setDescription] = useState(defaultDescription);
  const [state, setState] = useState((siteFacts?.state ?? "QLD").toUpperCase());
  const [powerlines, setPowerlines] = useState("unknown");
  const [loadSupport, setLoadSupport] = useState("manual");   // 27 Sep 2026 — our usual: riggers climb, no crane
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<{ chars: number; steps: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function generate() {
    if (!description.trim()) { setErr("Describe the work."); return; }
    setBusy(true); setErr(null);
    try {
      const draft = await streamSwmsDraft({
        work_description: description.trim(),
        state,
        site_context: [POWERLINE_OPTIONS[powerlines], LOAD_SUPPORT_OPTIONS[loadSupport], siteFacts?.context].filter(Boolean).join(". ") || undefined,
        base_content: existing ?? undefined,
      }, setProgress);
      onDraft(draft);
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
      setProgress(null);
    }
  }

  return (
    <ModalShell title="AI draft" onClose={onClose} wide>
      <div className="bg-ink rounded-md border border-line/50 p-3 text-[12px] text-text-muted leading-relaxed">
        Always a <b className="text-text">draft</b>: a competent person edits and a <b className="text-text">director
        approves</b> before anything is usable. Read every line like an incident investigator will one day.
      </div>
      {err && <div className="bg-danger/10 border border-danger/30 rounded px-3 py-2 text-[12px] text-danger">{err}</div>}
      <label className="block text-[12px] text-text-muted">Describe the work *
        <Textarea rows={4} value={description} onChange={e => setDescription(e.target.value)}/>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-[12px] text-text-muted">State (legislation)
          <Select value={state} onChange={e => setState(e.target.value)}>
            {["QLD", "NSW", "SA", "VIC", "WA", "TAS", "NT", "ACT"].map(s => <option key={s}>{s}</option>)}
          </Select>
        </label>
        <PowerlineSelect value={powerlines} onChange={setPowerlines}/>
        <LoadSupportSelect value={loadSupport} onChange={setLoadSupport}/>
      </div>
      {existing && (
        <div className="text-[12px] text-warn">Reworks the current content (keeping what's right) — nothing publishes until approval.</div>
      )}
      <Button onClick={generate} disabled={busy} icon={busy ? Loader2 : Sparkles} className="w-full justify-center">
        {busy
          ? progress
            ? `Writing the draft… step ${Math.max(progress.steps, 1)} · ${(progress.chars / 1000).toFixed(1)}k chars`
            : "Reading the job…"
          : existing ? "Rework the draft" : "Generate draft"}
      </Button>
      {busy && (
        <div className="text-[11px] text-text-muted text-center">
          A detailed job can take a few minutes — the counter above is the draft being written live. Leave this open.
        </div>
      )}
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4 pt-[8vh]">
      <div className={`bg-ink-raised border border-line rounded-lg w-full ${wide ? "max-w-[620px]" : "max-w-[440px]"}`}>
        <div className="px-4 py-3 border-b border-line flex items-center justify-between">
          <div className="text-[14px] font-semibold">{title}</div>
          <button type="button" onClick={onClose} className="text-text-muted hover:text-text p-1" aria-label="Close"><X size={15}/></button>
        </div>
        <div className="p-4 space-y-4">{children}</div>
      </div>
    </div>
  );
}
